import {QueryOptions} from '@apollo/client';
import Backbone from 'backbone';

import {ArchiveRecord} from 'archive/ArchiveConstants';
import BenchlingConfig from 'BenchlingConfig';
import * as statusbar from 'components/StatusBar';
import ModelTypeConstants from 'constants/ModelTypeConstants';
import ApolloFileState from 'filestate/ApolloFileState';
import {FileStateAttributes} from 'filestate/FileState';
import {isInaccessible} from 'graphql-client/InaccessibleInfo';
import {BufferState, BufferStates} from 'Navigation';

import {
  GetPlateDesignTemplate_Query as GetPlateDesignTemplateQuery,
  GetPlateDesignTemplateVariables,
} from './__generated/GetPlateDesignTemplate';
import {GET_PLATE_DESIGN_TEMPLATE_QUERY} from './PlateDesignTemplateQueries';
import PlateDesignTemplateTabState from './PlateDesignTemplateTabState';

type PlateDesignTemplate = GetPlateDesignTemplateQuery['plateDesignTemplate'];

interface PlateDesignTemplateFileStateAttributes {
  plateDesignTemplate?: PlateDesignTemplate;
  editUrl?: string;
  name?: string;
  archiveRecord?: ArchiveRecord | null;
}

class PlateDesignTemplateFileState extends ApolloFileState<
  GetPlateDesignTemplateQuery,
  GetPlateDesignTemplateVariables,
  PlateDesignTemplateFileStateAttributes
> {
  static fileType = ModelTypeConstants.PLATE_DESIGN_TEMPLATE;
  static override getQueryOptions(fileId: string): QueryOptions<GetPlateDesignTemplateVariables> {
    return {
      query: GET_PLATE_DESIGN_TEMPLATE_QUERY,
      variables: {
        id: fileId,
      },
    };
  }

  static override dataToAttributes(
    data: GetPlateDesignTemplateQuery
  ): PlateDesignTemplateFileStateAttributes {
    const {plateDesignTemplate} = data;
    return isInaccessible(plateDesignTemplate)
      ? {}
      : {
          plateDesignTemplate,
          editUrl: plateDesignTemplate.editUrl,
          name: plateDesignTemplate.name,
          archiveRecord: plateDesignTemplate.archiveRecord,
        };
  }

  static override openFile(fileId: string): JQueryPromise<PlateDesignTemplateFileState> {
    return super.openFile(fileId) as JQueryPromise<PlateDesignTemplateFileState>;
  }

  override defaults(): FileStateAttributes {
    return {
      ...super.defaults(),
      toolbar: new Backbone.Model(),
      statusbar: new statusbar.StatusBarState(),
      hideTabNav: BenchlingConfig.ENABLE_TEMPLATE_COLLECTIONS_IN_WORKSPACE,
    };
  }

  override initialize(...args: Array<unknown>): void {
    super.initialize(...args);

    const buffers = new BufferStates([new BufferState()]);
    this.set({buffers});
    this.buffers()
      .at(0)
      .tabs()
      .add(
        new PlateDesignTemplateTabState({
          plateDesignTemplateId: this.id,
        })
      );

    this.buffers().at(0).set({active: true});
    this.setIsWorkspaceSplit(false);
  }

  override editURL(): string | undefined {
    return this.get('editUrl');
  }

  override name(): string {
    return this.get('name') ?? 'Loading...';
  }

  override archiveRecord(): ArchiveRecord | null {
    return this.get('archiveRecord') ?? null;
  }

  override setArchiveRecord(archiveRecord: ArchiveRecord | null): void {
    this.set('archiveRecord', archiveRecord);
  }

  override setPermissions(): void {}

  override registerTitleAndURLSync(): void {
    this.on('change:name change:url', () => {
      this.syncTitleAndURL();
    });
  }
}

export default PlateDesignTemplateFileState;
